/* global REACT_APP_PUBLIC_ENABLE_TRACKING */
/* global REACT_APP_PUBLIC_POSTHOG_KEY */
/* global REACT_APP_PUBLIC_POSTHOG_HOST */
/* global REACT_APP_PUBLIC_SENTRY_DNS */
/* global REACT_APP_PUBLIC_SENTRY_ENVIRONMENT */

import { ChatwootProvider } from '@context/ChatwootContext';
import * as Sentry from '@sentry/react';
import { PostHogProvider } from 'posthog-js/react';
import { ReactNode, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import App from './App';
import { AuthComponent } from './context/AuthContext';
import { appStore } from './store';

import { registerSW } from 'virtual:pwa-register';
import './index.css';

// Register service worker
registerSW({
  onNeedRefresh() {
    // eslint-disable-next-line no-console
    console.log('[PWA] New content available, please refresh.');
    Sentry.captureMessage('PWA: New content available', {
      level: 'info',
      tags: {
        feature: 'pwa',
        action: 'need_refresh',
      },
    });
  },
  onOfflineReady() {
    // eslint-disable-next-line no-console
    console.log('[PWA] App ready to work offline');
    Sentry.captureMessage('PWA: App ready to work offline', {
      level: 'info',
      tags: {
        feature: 'pwa',
        action: 'offline_ready',
      },
    });
  },
  onRegistered(registration: ServiceWorkerRegistration | undefined) {
    // eslint-disable-next-line no-console
    console.log('[PWA] Service Worker registered:', registration);
    Sentry.captureMessage('PWA: Service Worker registered successfully', {
      level: 'info',
      tags: {
        feature: 'pwa',
        action: 'sw_registered',
      },
      extra: {
        scope: registration?.scope,
        active: registration?.active?.state,
        installing: registration?.installing?.state,
        waiting: registration?.waiting?.state,
      },
    });
  },
  onRegisterError(error: Error) {
    // eslint-disable-next-line no-console
    console.error('[PWA] Service Worker registration error:', error);
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        feature: 'pwa',
        action: 'sw_registration_error',
      },
      extra: {
        errorMessage: error.message,
        errorStack: error.stack,
      },
    });
  },
});

const rootElement = document.getElementById('app');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(rootElement!);

const isDev = REACT_APP_PUBLIC_SENTRY_ENVIRONMENT !== 'production';
const isProd = REACT_APP_PUBLIC_SENTRY_ENVIRONMENT === 'production';
const tracesSampleRate = isDev ? 1.0 : 0.7;
if (isProd) {
  Sentry.init({
    dsn: REACT_APP_PUBLIC_SENTRY_DNS,
    debug: false,
    environment: REACT_APP_PUBLIC_SENTRY_ENVIRONMENT,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracePropagationTargets: ['127.0.0.1', 'localhost', /^https:\/\/amazonaws\.com\//],
    // Performance Monitoring
    tracesSampleRate: tracesSampleRate,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const PostHogHOC = ({ children }: { children: ReactNode }) => {
  return !REACT_APP_PUBLIC_ENABLE_TRACKING ? (
    children
  ) : (
    <PostHogProvider
      apiKey={REACT_APP_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: REACT_APP_PUBLIC_POSTHOG_HOST,
        opt_in_site_apps: true,
      }}
    >
      {children}
    </PostHogProvider>
  );
};

root.render(
  <StrictMode>
    <Provider store={appStore}>
      <AuthComponent>
        <PostHogHOC>
          <ChatwootProvider>
            <App />
          </ChatwootProvider>
        </PostHogHOC>
      </AuthComponent>
    </Provider>
  </StrictMode>,
);
